import React from "react";

import type { GridApi, CellFocusedEvent, IRowNode } from "@/ui/molecules/Grid";

import ExtractionsSidebar from "@/ui/organisms/ExtractionsSidebar";

import type { ExtractionsErrorListProps } from "@/ui/organisms/ExtractionsSidebar/ExtractionsErrorList";
import type { TAllRowsAvailable } from "@/ui/organisms/typesGrids";
import type { SelectedColumn } from "@/ui/organisms/ExtractionsSidebar";
import type { TLabelsetInfo, TTableLocation } from "@/apis/odin/apiODIN";
import { isNotNullable } from "@/utils/guards";

type ViewExtractionsSidebarParams = {
    useState: ({
        extractionId,
        projectId,
        databaseId,
    }: {
        extractionId: number;
        projectId?: number;
        databaseId?: number;
    }) => {
        fileName: string | null;
        errorGroups: ExtractionsErrorListProps["errors"];
        highlightedCell: {
            rowId: number | string | null;
            colId: string | null;
        } | null;
        extractionStatus: "draft" | "completed" | null;
        selectedColumn: SelectedColumn | null;
        labelsetExtractionInfo: Array<TLabelsetInfo> | null;
        labelsetSourceId: number | null;
        showLabelset: boolean;
        selectedTable: TTableLocation | null;
    };
    useCases: ({
        extractionId,
        projectId,
        databaseId,
    }: {
        extractionId: number;
        projectId?: number;
        databaseId?: number;
    }) => {
        setHighlightedCell: (
            rowId: number | string | null,
            colId: string | null,
        ) => void;
        setHighlightedTargetRowIds: (rowIds: Array<number | string>) => void;
        onDismissWarnings?: (
            warnings: {
                rowId: number;
                periodId: number;
                accountLabel: string;
            }[],
        ) => void;
        onDismissDiagnosis?: (
            groups: { checkIdentities: string[]; dismissed: boolean }[],
        ) => void;
        onLabelsetSourceChange: (labelsetSourceId: number | null) => void;
    };
};

type ViewExtractionsSidebarProps = {
    extractionId: number;
    projectId?: number;
    databaseId?: number;
    gridApi: GridApi | null;
    description: string;
    allFoundTables: Array<TTableLocation> | null;
    canChangeTable?: boolean;
    onTableChange: (params: { table: TTableLocation }) => void;
    onLlmReExtract?: () => void;
    llmReExtractStatus?: "idle" | "pending" | "resolved" | "rejected";
    isFromLlm: boolean;
};

export const createViewExtractionsSidebar = ({
    useState,
    useCases,
}: ViewExtractionsSidebarParams) => {
    const ViewExtractionsSidebar = (props: ViewExtractionsSidebarProps) => {
        const {
            extractionId,
            projectId,
            databaseId,
            gridApi,
            description,
            allFoundTables,
            canChangeTable = true,
            onTableChange,
            onLlmReExtract,
            llmReExtractStatus,
            isFromLlm,
        } = props;

        const {
            fileName,
            errorGroups,
            highlightedCell,
            extractionStatus,
            selectedColumn,
            labelsetExtractionInfo,
            labelsetSourceId,
            showLabelset,
            selectedTable,
        } = useState({ extractionId, projectId, databaseId });
        const {
            setHighlightedCell,
            setHighlightedTargetRowIds,
            onDismissWarnings,
            onDismissDiagnosis,
            onLabelsetSourceChange,
        } = useCases({ extractionId, projectId, databaseId });

        const scrollToRowIdAndHighlightTheAccountCell = React.useCallback(
            (
                rowId: number | string | null,
                colId: string | null = "account",
            ) => {
                if (rowId === null) {
                    setHighlightedCell(null, null);
                    return;
                }
                if (!gridApi) return;
                const effectiveColId = colId ?? "account";
                const row = gridApi.getRowNode(String(rowId));
                const pinnedBottomRow = gridApi.getPinnedBottomRow(
                    rowId === "row-altered" ? 0 : 1,
                );
                const agGridRowIndex =
                    row?.rowIndex ?? pinnedBottomRow?.rowIndex;
                if (!isNotNullable(agGridRowIndex)) return;
                const column = gridApi.getColumn(effectiveColId);
                gridApi.ensureIndexVisible(agGridRowIndex);
                gridApi.ensureColumnVisible(effectiveColId);
                if (!column) return;

                gridApi.setFocusedCell(
                    agGridRowIndex,
                    column,
                    rowId === "row-altered" || rowId === "row-original"
                        ? "bottom"
                        : null,
                );
                setHighlightedCell(rowId, effectiveColId);
            },
            [gridApi, setHighlightedCell],
        );
        const highlightErrorOnCellFocusCallback = React.useCallback(
            (event: CellFocusedEvent) => {
                const { column, rowIndex: agGridRowIndex } = event;
                if (
                    column === null ||
                    typeof column === "string" ||
                    agGridRowIndex === null
                ) {
                    setHighlightedCell(null, null);
                    return;
                }
                const colId = column.getColId();
                if (!colId) return;
                const row = gridApi?.getDisplayedRowAtIndex(agGridRowIndex) as
                    | IRowNode<TAllRowsAvailable>
                    | undefined;
                const data = row?.data;
                const rowId = data?.rowId !== undefined ? data.rowId : null;
                setHighlightedCell(rowId, colId);
            },
            [gridApi, setHighlightedCell],
        );

        const subscribeToGridApiEvents = React.useCallback(() => {
            if (gridApi === null) return;
            gridApi.addEventListener(
                "cellFocused",
                highlightErrorOnCellFocusCallback,
            );
            return () => {
                gridApi.removeEventListener(
                    "cellFocused",
                    highlightErrorOnCellFocusCallback,
                );
            };
        }, [gridApi, highlightErrorOnCellFocusCallback]);

        React.useEffect(() => {
            const unsubscribe = subscribeToGridApiEvents();
            return unsubscribe;
        }, [subscribeToGridApiEvents]);

        return (
            <ExtractionsSidebar
                fileName={fileName ?? ""}
                description={description}
                allFoundTables={allFoundTables}
                canChangeTable={canChangeTable}
                selectedTable={selectedTable}
                onTableChange={(table: TTableLocation) =>
                    onTableChange({ table })
                }
                showErrors={extractionStatus === "draft"}
                showLabelset={showLabelset}
                selectedColumn={selectedColumn}
                labelsetExtractionInfo={labelsetExtractionInfo}
                labelsetSourceId={labelsetSourceId}
                onLabelsetSourceChange={onLabelsetSourceChange}
                onLlmReExtract={onLlmReExtract}
                llmReExtractStatus={llmReExtractStatus}
                isFromLlm={isFromLlm}
                ExtractionsErrorListProps={{
                    errors: errorGroups,
                    highlightedCell: highlightedCell,
                    onViewError: scrollToRowIdAndHighlightTheAccountCell,
                    onDismissWarnings: onDismissWarnings,
                    onHighlightTargetRows: setHighlightedTargetRowIds,
                    onDismissDiagnosis: onDismissDiagnosis,
                }}
            />
        );
    };

    return ViewExtractionsSidebar;
};
